import * as React from "react";
import { Accordion } from "react-bootstrap";
import { RouteProps, useRouteMatch } from "react-router-dom";
import store from "store2";

type OnCloseEvent = (
  e: React.MouseEvent<HTMLButtonElement, MouseEvent>
) => void;

interface PeekdownState {
  handleClose: OnCloseEvent;
}

const peekdownStore = store.namespace("peekdown");
const PeekdownContext = React.createContext<PeekdownState | undefined>(
  undefined
);

interface NSWPeekdownProps {
  id: string;
  storeKey?: string;
  match?: string | string[] | RouteProps;
}

export const NSWPeekdown: React.FC<NSWPeekdownProps> = ({
  id,
  storeKey = "name",
  match,
  children,
}) => {
  const routeMatch = useRouteMatch(match);

  const [activeKey, setActiveKey] = React.useState(null);

  const handleClose: OnCloseEvent = React.useCallback(
    (e) => {
      e.preventDefault();
      setActiveKey(null);
      peekdownStore.set(storeKey, id);
    },
    [id, storeKey]
  );

  React.useEffect(() => {
    const storedValue = peekdownStore.get(storeKey);

    if (storedValue !== id) {
      setActiveKey(id);
    }
  }, [id, storeKey]);

  if (match !== undefined && routeMatch && !routeMatch.isExact) {
    return null;
  }

  return (
    <PeekdownContext.Provider value={{ handleClose }}>
      <Accordion activeKey={activeKey}>
        <Accordion.Collapse eventKey={id}>
          {children as JSX.Element}
        </Accordion.Collapse>
      </Accordion>
    </PeekdownContext.Provider>
  );
};

const CloseBtn = (): JSX.Element => {
  const { handleClose } = React.useContext(PeekdownContext);

  return (
    <button
      type="button"
      onClick={handleClose}
      className="text-secondary bg-transparent border-0 p-3"
    >
      <svg
        width="18"
        height="18"
        aria-hidden="true"
        focusable="false"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 320 512"
      >
        <path
          fill="currentColor"
          d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z"
        />
      </svg>
      <span className="sr-only">Close peekdown</span>
    </button>
  );
};

export const EmergencyPeekdown = (): JSX.Element => {
  return (
    <section
      css={`
        position: relative;
        overflow: hidden;
        display: flex;
        align-items: center;
      `}
      className="text-secondary bg-warning"
    >
      <div className="container pr-0 text-center d-flex align-items-center justify-content-center">
        <svg
          viewBox="0 0 24 24"
          focusable="false"
          fill="currentColor"
          width="24"
          height="24"
          role="img"
          className="mr-2"
        >
          <path d="M12 1C5.9 1 1 5.9 1 12s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm0 20c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z"></path>
          <path d="M12 11c-.6 0-1 .4-1 1v4c0 .6.4 1 1 1s1-.4 1-1v-4c0-.6-.4-1-1-1zm.9-3.4c0-.1-.1-.1-.1-.2s-.1-.1-.1-.1c-.3-.3-.7-.4-1.1-.2-.1.1-.2.1-.3.2l-.1.1c0 .1-.1.1-.1.2s0 .1-.1.2V8c0 .3.1.5.3.7.1.1.2.2.3.2.1.1.3.1.4.1h.2c.1 0 .1 0 .2-.1.1 0 .1-.1.2-.1l.1-.1c.2-.2.3-.4.3-.7v-.2c0-.1-.1-.1-.1-.2z"></path>
        </svg>
        <p className="font-weight-bold mb-0 mr-3">
          NSW floods emergency support volunteering
        </p>
        <a
          className="btn btn-secondary btn-sm font-weight-bold"
          href="https://emergency.volunteer.org.au/campaign/4/nsw-floods#register"
          target="_blank"
          rel="noopener noreferrer"
        >
          Register now
        </a>
      </div>
      <CloseBtn />
    </section>
  );
};
